/**
 * Championnat par Équipes
 *
 * Layout type CIA : MasterDetail (sidebar phase + divisions, zone centrale avec onglets).
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import ClickablePseudo from '../../components/ClickablePseudo';
import ClickableTeamName from '../../components/ClickableTeamName';
import MasterDetail from '../../components/MasterDetail';
import { DivisionForumPanel, ciaPhaseGeneralForumBucketId } from '../../components/competitions/DivisionForumPanel';
import TeamMatchReplayView from '../../components/competitions/TeamMatchReplayView';
import { useResponsive } from '../../hooks/useResponsive';
import type { Session } from '../../types/app';
import { BACKEND_URL } from '../../config';

// ============================================
// Types
// ============================================

interface Phase {
  id: number;
  nom: string;
  etat: string;
  debut: string;
  fin: string;
  mode?: string;
}

interface Division {
  id: number;
  nom: string;
  index: number;
  hierarchie: number;
  type: string;
  nb_participants: number;
  nb_matches_played: number;
}

interface TeamStaffFields {
  staff_pseudo?: string | null;
  staff_display?: string | null;
  staff_role?: 'capitaine' | 'lieutenant' | null;
}

interface StandingItem extends TeamStaffFields {
  id: number;
  equipe_id: number;
  equipe_nom: string;
  classement: number;
  point: number;
  point_average_particulier: number | null;
  point_average_general: number | null;
  victoires: number;
  nuls: number;
  defaites: number;
  forfaits: number;
  joues: number;
  resultat: string;
}

interface ResultItem {
  id: number;
  equipe1_id: number | null;
  equipe2_id: number | null;
  equipe1_nom: string;
  equipe2_nom: string;
  equipe1_staff_pseudo?: string | null;
  equipe1_staff_display?: string | null;
  equipe1_staff_role?: 'capitaine' | 'lieutenant' | null;
  equipe2_staff_pseudo?: string | null;
  equipe2_staff_display?: string | null;
  equipe2_staff_role?: 'capitaine' | 'lieutenant' | null;
  etat: string;
  pm1: number | null;
  pm2: number | null;
  date: string | null;
  me1: number | null;
  me2: number | null;
  match_id: number | null;
}

interface CalendarItem {
  id: number;
  equipe1_id: number | null;
  equipe2_id: number | null;
  equipe1_nom: string;
  equipe2_nom: string;
  equipe1_staff_pseudo?: string | null;
  equipe1_staff_display?: string | null;
  equipe1_staff_role?: 'capitaine' | 'lieutenant' | null;
  equipe2_staff_pseudo?: string | null;
  equipe2_staff_display?: string | null;
  equipe2_staff_role?: 'capitaine' | 'lieutenant' | null;
  rdv: string;
  etat: string;
}

interface TeamMember {
  pseudo: string;
  pseudo_display: string;
}

interface TeamItem extends TeamStaffFields {
  id: number;
  equipe_id: number;
  equipe_nom: string;
  classement: number;
  resultat: string;
  members: TeamMember[];
}

/** Équipe dans la composition « toute la phase » (présentation générale) */
interface PhaseTeamRosterItem extends TeamItem {
  division_id: number;
  division_nom: string;
}

interface TCOverview {
  competition: { id: number; nom: string; email?: string } | null;
  current_phase: Phase | null;
  phases: Phase[];
  organizers: { pseudo: string; pseudo_display: string; role?: string }[];
}

type TabId = 'standings' | 'results' | 'calendar' | 'composition' | 'forum';

type GeneralTabId = 'composition' | 'forum';

type SidebarSelection = 'general' | number;

interface TeamChampionshipPageProps {
  session: Session;
  isAdmin?: boolean;
}

/** Match dont on consulte les donnes depuis l'onglet Résultats. */
interface SelectedTeamMatch {
  matchId: number;
  equipe1Nom: string;
  equipe2Nom: string;
  equipe1Id?: number;
  equipe2Id?: number;
  matchDate?: string | null;
}

// ============================================
// Helpers
// ============================================

function phaseStateLabel(etat: string): string {
  switch (etat) {
    case 'encours': return 'En cours';
    case 'barrage': return 'Barrages';
    case 'fini': return 'Terminée';
    case 'preparation': return 'Préparation';
    default: return etat;
  }
}

function formatDateTime(d: string | null | undefined): string {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch { return '-'; }
}

function resultatIcon(r: string): string {
  switch (r) {
    case 'montee': return '\u2191';
    case 'descente': return '\u2193';
    case 'abandon': return '\u2717';
    default: return '';
  }
}

function normPseudo(p: string): string {
  return p.trim().toLowerCase();
}

/** Pour le forum : nom d’équipe dans la phase / poule (composition championnat), pas l’équipe hors compétition. */
function buildPseudoToTeamNameMap(teams: TeamItem[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of teams) {
    const label = (t.equipe_nom || '').trim();
    if (!label) continue;
    const add = (p: string | null | undefined) => {
      const key = normPseudo(p || '');
      if (key) m.set(key, label);
    };
    add(t.staff_pseudo);
    for (const mem of t.members || []) add(mem.pseudo);
  }
  return m;
}

function fmtPm(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return (Math.round(Number(v) * 1000) / 1000).toFixed(3);
}

function teamPairKey(eidA: number, eidB: number): string {
  const a = Math.min(eidA, eidB);
  const b = Math.max(eidA, eidB);
  return `${a}\0${b}`;
}

/** Index des paires de résultats par identifiants d'équipe */
function buildTeamResultPairMap(items: ResultItem[]): Map<string, ResultItem> {
  const m = new Map<string, ResultItem>();
  for (const it of items) {
    if (it.equipe1_id == null || it.equipe2_id == null) continue;
    m.set(teamPairKey(it.equipe1_id, it.equipe2_id), it);
  }
  return m;
}

function teamAvatarPhotoUrl(equipeNom: string | null | undefined, equipeId: number, size: number): string {
  const key = (equipeNom && equipeNom.trim()) ? equipeNom.trim() : `Équipe #${equipeId}`;
  return `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(key)}?size=${size * 2}`;
}

/** Bloc avatar équipe + nom ; contact staff (capitaine ou lieutenant) optionnel sous le nom */
function TeamBlock({
  equipeNom,
  equipeId,
  staffPseudo,
  staffDisplay,
  staffRole,
  showStaffUnderName,
  size = 36,
}: {
  equipeNom: string | null | undefined;
  equipeId: number;
  staffPseudo?: string | null;
  staffDisplay?: string | null;
  staffRole?: 'capitaine' | 'lieutenant' | null;
  showStaffUnderName: boolean;
  size?: number;
}) {
  const label = (equipeNom && equipeNom.trim()) ? equipeNom.trim() : `Équipe #${equipeId}`;
  const photoUrl = teamAvatarPhotoUrl(equipeNom, equipeId, size);
  const staffPrefix = staffRole === 'lieutenant' ? 'Lt.' : 'Cap.';
  return (
    <div className="tc-team-block">
      <PlayerAvatar pseudo={label} size={size} photoUrl={photoUrl} />
      <div className="tc-team-block-text">
        <ClickableTeamName teamId={equipeId} className="tc-team-block-name">
          {label}
        </ClickableTeamName>
        {showStaffUnderName && staffDisplay && staffRole && (
          <span className="tc-team-block-cap">
            {staffPrefix} :{' '}
            {staffPseudo ? (
              <ClickablePseudo pseudo={staffPseudo}>{staffDisplay}</ClickablePseudo>
            ) : (
              staffDisplay
            )}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================
// Cross-tab matrix (même présentation que le CIA)
// ============================================

function TeamCrossTabResults({
  standings,
  results,
  viewerEquipeId,
  isAdmin,
  onOpenMatch,
}: {
  standings: StandingItem[];
  results: ResultItem[];
  viewerEquipeId: number | null;
  isAdmin: boolean;
  onOpenMatch: (match: SelectedTeamMatch) => void;
}) {
  const teams = useMemo(
    () => [...standings].sort((a, b) => a.classement - b.classement),
    [standings],
  );
  const pairMap = useMemo(() => buildTeamResultPairMap(results), [results]);

  if (teams.length === 0) {
    return <div className="compet-empty">Aucune équipe dans ce groupe pour afficher la matrice</div>;
  }

  function cellContent(row: StandingItem, col: StandingItem): ReactNode {
    if (row.equipe_id === col.equipe_id) {
      return <span className="cia-matrix-diag-mark">—</span>;
    }
    const key = teamPairKey(row.equipe_id, col.equipe_id);
    const r = pairMap.get(key);
    if (!r) return null;

    if (r.etat.startsWith('forfait')) {
      return <span className="cia-matrix-forfeit">Forfait</span>;
    }

    const rowIsE1 = r.equipe1_id === row.equipe_id;
    const rowPm = rowIsE1 ? r.pm1 : r.pm2;
    const colPm = rowIsE1 ? r.pm2 : r.pm1;
    return (
      <div className="cia-matrix-scores">
        <span>{fmtPm(rowPm)}</span>
        <span className="cia-matrix-sep">—</span>
        <span>{fmtPm(colPm)}</span>
      </div>
    );
  }

  return (
    <div className="cia-matrix-wrap">
      <table className="cia-matrix-table">
        <thead>
          <tr>
            <th className="cia-matrix-corner" />
            {teams.map((col) => {
              const label = (col.equipe_nom && col.equipe_nom.trim()) ? col.equipe_nom.trim() : `Équipe #${col.equipe_id}`;
              const photoUrl = teamAvatarPhotoUrl(col.equipe_nom, col.equipe_id, 22);
              return (
                <th key={col.equipe_id} className="cia-matrix-col-head">
                  <div className="cia-matrix-col-inner">
                    <PlayerAvatar pseudo={label} size={22} photoUrl={photoUrl} />
                    <ClickableTeamName teamId={col.equipe_id} className="cia-matrix-col-name" title={label}>
                      {label}
                    </ClickableTeamName>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {teams.map((row) => (
            <tr key={row.equipe_id}>
              <th className="cia-matrix-row-head">
                <TeamBlock
                  equipeNom={row.equipe_nom}
                  equipeId={row.equipe_id}
                  staffPseudo={row.staff_pseudo}
                  staffDisplay={row.staff_display ?? null}
                  staffRole={row.staff_role ?? null}
                  showStaffUnderName
                  size={24}
                />
              </th>
              {teams.map((col) => {
                const a = row.equipe_id;
                const b = col.equipe_id;
                const pairKey = teamPairKey(a, b);
                const match = a !== b ? pairMap.get(pairKey) : undefined;
                const hasMatch = Boolean(match);
                // Un membre ne peut ouvrir que les matchs de son équipe ;
                // les admins ont accès à tous les matchs.
                const canOpen = Boolean(
                  match?.match_id
                  && !match.etat.startsWith('forfait')
                  && (isAdmin || a === viewerEquipeId || b === viewerEquipeId),
                );
                const tdClass = [
                  'cia-matrix-cell',
                  a === b ? 'cia-matrix-diag' : '',
                  a !== b && !hasMatch ? 'cia-matrix-empty' : '',
                  canOpen ? 'cia-matrix-cell--replayable' : '',
                ].filter(Boolean).join(' ');
                return (
                  <td
                    key={col.equipe_id}
                    className={tdClass}
                    onClick={canOpen
                      ? () => onOpenMatch({
                          matchId: Number(match!.match_id),
                          equipe1Nom: match!.equipe1_nom || row.equipe_nom || `Équipe #${a}`,
                          equipe2Nom: match!.equipe2_nom || col.equipe_nom || `Équipe #${b}`,
                          equipe1Id: match!.equipe1_id != null ? Number(match!.equipe1_id) : a,
                          equipe2Id: match!.equipe2_id != null ? Number(match!.equipe2_id) : b,
                          matchDate: match!.date,
                        })
                      : undefined}
                    title={canOpen ? 'Voir les donnes jouées' : undefined}
                  >
                    {cellContent(row, col)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Component
// ============================================

export default function TeamChampionshipPage({ session, isAdmin = false }: TeamChampionshipPageProps) {
  const { isCompact } = useResponsive();
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const myDivisionButtonRef = useRef<HTMLButtonElement | null>(null);

  const [overview, setOverview] = useState<TCOverview | null>(null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(null);
  const [divisions, setDivisions] = useState<Division[]>([]);
  /** Division dans laquelle l’équipe du joueur est engagée pour la phase sélectionnée (si applicable). */
  const [userDivisionIdInPhase, setUserDivisionIdInPhase] = useState<number | null>(null);
  /** Équipe du joueur pour la phase sélectionnée : autorise l'accès aux donnes de ses matchs. */
  const [userEquipeIdInPhase, setUserEquipeIdInPhase] = useState<number | null>(null);
  /** Match dont on consulte les donnes (onglet Résultats). */
  const [selectedMatch, setSelectedMatch] = useState<SelectedTeamMatch | null>(null);
  /** Poule choisie ou « Présentation générale » */
  const [sidebarSelection, setSidebarSelection] = useState<SidebarSelection | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('standings');
  const [generalTab, setGeneralTab] = useState<GeneralTabId>('composition');

  const [standings, setStandings] = useState<StandingItem[]>([]);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [calendar, setCalendar] = useState<CalendarItem[]>([]);
  const [teams, setTeams] = useState<TeamItem[]>([]);
  const [phaseRoster, setPhaseRoster] = useState<PhaseTeamRosterItem[]>([]);
  const authorTeamNameLookup = useMemo(() => buildPseudoToTeamNameMap(teams), [teams]);
  const phaseForumTeamLookup = useMemo(() => buildPseudoToTeamNameMap(phaseRoster), [phaseRoster]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load overview
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const url = `${BACKEND_URL}/api/competition/team-champ`;
        const res = await fetch(url);
        const json = await res.json();
        if (!json.success) throw new Error(json.details ? `${json.error}: ${json.details}` : json.error);
        const data = json.data as TCOverview;
        setOverview(data);
        setPhases(data.phases || []);
        if (data.current_phase) {
          setSelectedPhaseId(data.current_phase.id);
        } else if (data.phases?.length) {
          setSelectedPhaseId(data.phases[0].id);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load divisions when phase changes (+ division « ma » pour les droits forum)
  useEffect(() => {
    if (!selectedPhaseId) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/competition/team-champ/${selectedPhaseId}/divisions`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        const items: Division[] = json.data?.items ?? [];

        let myDiv: number | null = null;
        let myEquipe: number | null = null;
        try {
          const meRes = await fetch(
            `${BACKEND_URL}/api/competition/team-champ/me?phase_id=${selectedPhaseId}`,
            { headers: { 'X-User-Pseudo': session.pseudo } },
          );
          const meJson = await meRes.json();
          if (meJson.success && meJson.data?.participant?.division_id != null) {
            myDiv = Number(meJson.data.participant.division_id);
          }
          if (meJson.success && meJson.data?.participant?.equipe_id != null) {
            myEquipe = Number(meJson.data.participant.equipe_id);
          }
        } catch {
          /* non connecté ou erreur */
        }
        setUserEquipeIdInPhase(myEquipe);

        const myDivisionInList =
          myDiv != null && items.some((d) => d.id === myDiv) ? myDiv : null;

        setDivisions(items);
        setUserDivisionIdInPhase(myDivisionInList);

        if (items.length > 0) {
          setSidebarSelection(myDivisionInList ?? 'general');
        } else {
          setSidebarSelection(null);
          setShowMobileDetail(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement des divisions');
      }
    })();
  }, [selectedPhaseId, session.pseudo]);

  useLayoutEffect(() => {
    if (userDivisionIdInPhase == null || divisions.length === 0) return;
    if (sidebarSelection !== userDivisionIdInPhase) return;
    myDivisionButtonRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedPhaseId, divisions, userDivisionIdInPhase, sidebarSelection]);

  const selectedDivisionId =
    sidebarSelection !== null && sidebarSelection !== 'general' ? sidebarSelection : null;

  const selectDivision = useCallback(
    (id: number) => {
      setSidebarSelection(id);
      if (isCompact) setShowMobileDetail(true);
    },
    [isCompact]
  );

  const selectGeneralPresentation = useCallback(() => {
    setSidebarSelection('general');
    if (isCompact) setShowMobileDetail(true);
  }, [isCompact]);

  const onPhaseChange = useCallback((id: number) => {
    setSelectedPhaseId(id);
    setShowMobileDetail(false);
  }, []);

  // Load tab data
  const loadTabData = useCallback(async () => {
    if (!selectedPhaseId || sidebarSelection === null) return;

    if (sidebarSelection === 'general') {
      try {
        const res = await fetch(`${BACKEND_URL}/api/competition/team-champ/${selectedPhaseId}/roster`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setPhaseRoster(json.data?.items ?? []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      }
      return;
    }

    const divId = sidebarSelection;

    try {
      if (activeTab === 'standings') {
        const res = await fetch(
          `${BACKEND_URL}/api/competition/team-champ/${selectedPhaseId}/divisions/${divId}/standings`
        );
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setStandings(json.data?.items ?? []);
      } else if (activeTab === 'results') {
        const [standingsRes, resultsRes] = await Promise.all([
          fetch(
            `${BACKEND_URL}/api/competition/team-champ/${selectedPhaseId}/divisions/${divId}/standings`,
          ),
          fetch(
            `${BACKEND_URL}/api/competition/team-champ/${selectedPhaseId}/divisions/${divId}/results?limit=200`,
          ),
        ]);
        const standingsJson = await standingsRes.json();
        const resultsJson = await resultsRes.json();
        if (!standingsJson.success) throw new Error(standingsJson.error);
        if (!resultsJson.success) throw new Error(resultsJson.error);
        setStandings(standingsJson.data?.items ?? []);
        setResults(resultsJson.data?.items ?? []);
      } else if (activeTab === 'calendar') {
        const res = await fetch(
          `${BACKEND_URL}/api/competition/team-champ/${selectedPhaseId}/divisions/${divId}/calendar`
        );
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setCalendar(json.data?.items ?? []);
      } else if (activeTab === 'composition' || activeTab === 'forum') {
        const res = await fetch(
          `${BACKEND_URL}/api/competition/team-champ/${selectedPhaseId}/divisions/${divId}/teams`
        );
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setTeams(json.data?.items ?? []);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    }
  }, [selectedPhaseId, sidebarSelection, activeTab]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData, activeTab, sidebarSelection]);

  // Quitter la vue des donnes dès qu'on change de contexte
  useEffect(() => {
    setSelectedMatch(null);
  }, [selectedPhaseId, sidebarSelection, activeTab]);

  if (loading && !overview) {
    return <div className="compet-loading">Chargement...</div>;
  }

  if (error && !overview) {
    return <div className="compet-error">{error}</div>;
  }

  const sidebarTitle =
    overview?.competition?.nom?.trim() || 'Championnat par équipes';

  const sidebar = (
    <div className="cia-sidebar">
      <div className="cia-sidebar-title">{sidebarTitle}</div>
      <label className="cia-sidebar-label">Phase</label>
      <select
        className="cia-sidebar-select"
        value={selectedPhaseId ?? ''}
        onChange={(e) => onPhaseChange(Number(e.target.value))}
      >
        {phases.map((p) => (
          <option key={p.id} value={p.id}>
            {p.nom} ({phaseStateLabel(p.etat)})
          </option>
        ))}
      </select>
      <label className="cia-sidebar-label">Division</label>
      <div className="cia-sidebar-divisions">
        {divisions.length === 0 ? (
          <div className="compet-empty" style={{ padding: '12px 0' }}>
            Aucune division pour cette phase
          </div>
        ) : (
          <>
            <button
              type="button"
              className={[
                'cia-division-item',
                'cia-division-item--general',
                sidebarSelection === 'general' ? 'active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={selectGeneralPresentation}
            >
              <span className="cia-division-name">Présentation générale</span>
            </button>
            {divisions.map((d) => {
              const isMyDivision = userDivisionIdInPhase === d.id;
              return (
                <button
                  key={d.id}
                  ref={isMyDivision ? myDivisionButtonRef : undefined}
                  type="button"
                  className={[
                    'cia-division-item',
                    sidebarSelection === d.id ? 'active' : '',
                    isMyDivision ? 'cia-division-item--mine' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  title={`${d.nb_participants} équipes, ${d.nb_matches_played} matchs joués`}
                  onClick={() => selectDivision(d.id)}
                >
                  <span className="cia-division-item-inner">
                    <span className="cia-division-name">{d.nom}</span>
                    {isMyDivision && (
                      <span className="cia-division-mine-badge" title="Votre poule pour cette phase">
                        Ma poule
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </div>
  );

  const detailTitle =
    sidebarSelection === 'general'
      ? 'Présentation générale'
      : selectedDivisionId != null
        ? divisions.find((d) => d.id === selectedDivisionId)?.nom ?? sidebarTitle
        : sidebarTitle;

  const detailContent =
    sidebarSelection === null ? (
      <div className="compet-empty">Sélectionnez une entrée dans la colonne de gauche</div>
    ) : sidebarSelection === 'general' ? (
      <>
        <div className="compet-tabs">
          <button
            type="button"
            className={`compet-tab ${generalTab === 'composition' ? 'active' : ''}`}
            onClick={() => setGeneralTab('composition')}
          >
            Composition
          </button>
          <button
            type="button"
            className={`compet-tab ${generalTab === 'forum' ? 'active' : ''}`}
            onClick={() => setGeneralTab('forum')}
          >
            Forum général
          </button>
        </div>
        <div
          className={[
            'compet-content',
            'cia-detail-content',
            generalTab === 'forum' ? 'cia-detail-content--forum' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {error && overview && generalTab === 'composition' && (
            <div className="compet-error" style={{ marginBottom: 8 }}>{error}</div>
          )}
          {generalTab === 'composition' && <TeamCompositionTab items={phaseRoster} />}
          {generalTab === 'forum' && selectedPhaseId != null && (
            <DivisionForumPanel
              divisionId={0}
              phaseId={selectedPhaseId}
              forumBucketOverride={ciaPhaseGeneralForumBucketId(selectedPhaseId)}
              viewerPseudo={session.pseudo}
              authorTeamNameLookup={phaseForumTeamLookup}
              canPost={userDivisionIdInPhase !== null}
            />
          )}
        </div>
      </>
    ) : (
      <>
        <div className="compet-tabs">
          <button
            type="button"
            className={`compet-tab ${activeTab === 'standings' ? 'active' : ''}`}
            onClick={() => setActiveTab('standings')}
          >
            Classement
          </button>
          <button
            type="button"
            className={`compet-tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            Résultats
          </button>
          <button
            type="button"
            className={`compet-tab ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            Calendrier
          </button>
          <button
            type="button"
            className={`compet-tab ${activeTab === 'composition' ? 'active' : ''}`}
            onClick={() => setActiveTab('composition')}
          >
            Composition
          </button>
          <button
            type="button"
            className={`compet-tab ${activeTab === 'forum' ? 'active' : ''}`}
            onClick={() => setActiveTab('forum')}
          >
            Forum
          </button>
        </div>
        <div
          className={[
            'compet-content',
            'cia-detail-content',
            activeTab === 'forum'
              ? 'cia-detail-content--forum'
              : activeTab === 'results'
                ? 'cia-detail-content--results'
                : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {error && overview && <div className="compet-error" style={{ marginBottom: 8 }}>{error}</div>}
          {activeTab === 'standings' && <TeamStandingsTab items={standings} />}
          {activeTab === 'results' && (
            selectedMatch ? (
              <TeamMatchReplayView
                matchId={selectedMatch.matchId}
                equipe1Nom={selectedMatch.equipe1Nom}
                equipe2Nom={selectedMatch.equipe2Nom}
                equipe1Id={selectedMatch.equipe1Id}
                equipe2Id={selectedMatch.equipe2Id}
                matchDate={selectedMatch.matchDate}
                phaseId={selectedPhaseId}
                divisionId={selectedDivisionId}
                session={session}
                onBack={() => setSelectedMatch(null)}
              />
            ) : (
              <TeamCrossTabResults
                standings={standings}
                results={results}
                viewerEquipeId={userEquipeIdInPhase}
                isAdmin={isAdmin}
                onOpenMatch={setSelectedMatch}
              />
            )
          )}
          {activeTab === 'calendar' && <TeamCalendarTab items={calendar} />}
          {activeTab === 'composition' && <TeamCompositionTab items={teams} />}
          {activeTab === 'forum' && selectedPhaseId != null && selectedDivisionId != null && (
            <DivisionForumPanel
              divisionId={selectedDivisionId}
              phaseId={selectedPhaseId}
              viewerPseudo={session.pseudo}
              authorTeamNameLookup={authorTeamNameLookup}
              canPost={
                userDivisionIdInPhase !== null &&
                selectedDivisionId === userDivisionIdInPhase
              }
            />
          )}
        </div>
      </>
    );

  return (
    <div className="chat-container cia-page-root">
      <MasterDetail
        master={sidebar}
        detail={detailContent}
        detailTitle={detailTitle}
        hasSelection={isCompact && showMobileDetail && sidebarSelection !== null}
        onBack={() => setShowMobileDetail(false)}
      />
    </div>
  );
}

// ============================================
// Sub-components
// ============================================

function TeamStandingsTab({ items }: { items: StandingItem[] }) {
  if (items.length === 0) return <div className="compet-empty">Aucun classement disponible</div>;

  return (
    <table className="compet-standings-table tc-standings-teams">
      <thead>
        <tr>
          <th className="rank-col">#</th>
          <th>Équipe</th>
          <th className="num-col">Pts</th>
          <th className="num-col">J</th>
          <th className="num-col">V</th>
          <th className="num-col">N</th>
          <th className="num-col">D</th>
          <th className="num-col">F</th>
          <th className="num-col">PM Part.</th>
          <th className="num-col">PM Gén.</th>
          <th style={{ width: 30, textAlign: 'center' }}></th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <tr key={item.id}>
            <td className="rank-col">{item.classement}</td>
            <td>
              <TeamBlock
                equipeNom={item.equipe_nom}
                equipeId={item.equipe_id}
                staffPseudo={item.staff_pseudo}
                staffDisplay={item.staff_display ?? null}
                staffRole={item.staff_role ?? null}
                showStaffUnderName
                size={34}
              />
            </td>
            <td className="num-col">{item.point}</td>
            <td className="num-col">{item.joues}</td>
            <td className="num-col">{item.victoires}</td>
            <td className="num-col">{item.nuls}</td>
            <td className="num-col">{item.defaites}</td>
            <td className="num-col">{item.forfaits || '-'}</td>
            <td className="num-col">
              {item.point_average_particulier != null
                ? (Math.round(item.point_average_particulier * 100) / 100).toFixed(2)
                : '-'}
            </td>
            <td className="num-col">
              {item.point_average_general != null
                ? (Math.round(item.point_average_general * 100) / 100).toFixed(2)
                : '-'}
            </td>
            <td style={{ textAlign: 'center' }}>
              {resultatIcon(item.resultat)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TeamCalendarTab({ items }: { items: CalendarItem[] }) {
  if (items.length === 0) {
    return (
      <div className="compet-empty">
        Aucune rencontre planifiée (défis à l&apos;état « ok » avec date de rendez-vous future).
      </div>
    );
  }

  return (
    <table className="compet-standings-table tc-calendar-table">
      <thead>
        <tr>
          <th>Rendez-vous</th>
          <th>Équipe 1</th>
          <th></th>
          <th>Équipe 2</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => {
          const e1id = item.equipe1_id ?? 0;
          const e2id = item.equipe2_id ?? 0;
          return (
            <tr key={item.id}>
              <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatDateTime(item.rdv)}</td>
              <td>
                <TeamBlock
                  equipeNom={item.equipe1_nom}
                  equipeId={e1id}
                  staffPseudo={item.equipe1_staff_pseudo}
                  staffDisplay={item.equipe1_staff_display ?? null}
                  staffRole={item.equipe1_staff_role ?? null}
                  showStaffUnderName
                  size={32}
                />
              </td>
              <td className="num-col" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>vs</td>
              <td>
                <TeamBlock
                  equipeNom={item.equipe2_nom}
                  equipeId={e2id}
                  staffPseudo={item.equipe2_staff_pseudo}
                  staffDisplay={item.equipe2_staff_display ?? null}
                  staffRole={item.equipe2_staff_role ?? null}
                  showStaffUnderName
                  size={32}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function sortMembersForComposition(team: TeamItem): TeamMember[] {
  const staffKey = team.staff_pseudo ? normPseudo(team.staff_pseudo) : '';
  return [...team.members].sort((a, b) => {
    const aP = normPseudo(a.pseudo);
    const bP = normPseudo(b.pseudo);
    const rank = (p: string) => (staffKey && p === staffKey ? 0 : 1);
    const d = rank(aP) - rank(bP);
    if (d !== 0) return d;
    return a.pseudo_display.localeCompare(b.pseudo_display, 'fr', { sensitivity: 'base' });
  });
}

function TeamCompositionTab({ items }: { items: Array<TeamItem & { division_nom?: string }> }) {
  if (items.length === 0) return <div className="compet-empty">Aucune équipe inscrite</div>;

  return (
    <div className="tc-teams-grid">
      {items.map(team => {
        const staffKey = team.staff_pseudo ? normPseudo(team.staff_pseudo) : '';
        const ordered = sortMembersForComposition(team);
        const label = (team.equipe_nom && team.equipe_nom.trim()) ? team.equipe_nom.trim() : `Équipe #${team.equipe_id}`;
        const photoUrl = teamAvatarPhotoUrl(team.equipe_nom, team.equipe_id, 40);
        const poule = team.division_nom?.trim();
        return (
          <div key={team.id} className="tc-team-card">
            {poule ? (
              <div className="tc-phase-team-poule" title="Poule">
                {poule}
              </div>
            ) : null}
            <div className="tc-team-header tc-team-header--composition">
              <PlayerAvatar pseudo={label} size={40} photoUrl={photoUrl} />
              <div className="tc-team-header-text">
                <ClickableTeamName teamId={team.equipe_id} className="tc-team-name">
                  {label}
                </ClickableTeamName>
                {team.resultat && team.resultat !== 'null' && (
                  <span className="tc-team-status">{resultatIcon(team.resultat)}</span>
                )}
              </div>
            </div>
            <div className="tc-team-members">
              {ordered.map(m => {
                const isStaff = staffKey && normPseudo(m.pseudo) === staffKey;
                return (
                  <div
                    key={m.pseudo}
                    className={[
                      'tc-team-member',
                      isStaff && team.staff_role === 'capitaine' ? 'tc-team-member--capitaine' : '',
                      isStaff && team.staff_role === 'lieutenant' ? 'tc-team-member--lieutenant' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <PlayerAvatar pseudo={m.pseudo} size={22} />
                    <ClickablePseudo pseudo={m.pseudo}>
                      {m.pseudo_display}
                    </ClickablePseudo>
                    {isStaff && team.staff_role === 'capitaine' && (
                      <span className="tc-role-badge tc-role-badge--cap">Capitaine</span>
                    )}
                    {isStaff && team.staff_role === 'lieutenant' && (
                      <span className="tc-role-badge tc-role-badge--lt">Lieutenant</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
